import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { autosProtocolDecode } from '../data/mockData';
import ProtocolBanner from '../components/economics/ProtocolBanner';
import ValueLoop from '../components/economics/ValueLoop';
import ArchBlueprint from '../components/economics/ArchBlueprint';
import CompliancePanel from '../components/economics/CompliancePanel';
import YieldMeter from '../components/economics/YieldMeter';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

export default function Economics() {
  const [activeLetter, setActiveLetter] = useState(null);

  return (
    <div className="min-h-screen pt-28 pb-20 px-6 max-w-6xl mx-auto">
      {/* Protocol Banner */}
      <div className="mb-12">
        <ProtocolBanner />
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="flex items-center gap-2 text-amber-400 mb-3">
          <SafeIcon icon={FiIcons.FiDollarSign} />
          <span className="text-xs font-bold tracking-widest uppercase">/// NSTF Economics</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-zinc-100 mb-4">
          Non-Speculative <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-cyan-400">
            Tangible Framework
          </span>
        </h1>
        <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed">
          Hammurabi Medallions are debt-free tokens of exchange anchored by real-world enterprise demand: 
          the hunger for high-fidelity, unpolluted data. No speculative casino. No vaporware. 
          Just sovereign data yield.
        </p>
      </motion.div>

      {/* A.U.T.O.S. Protocol Decode */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-16"
      >
        <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiKey} /> Protocol Decode: A.U.T.O.S.
        </h2>
        <div className="flex flex-wrap gap-3">
          {autosProtocolDecode.map((item, i) => (
            <button
              key={i}
              onMouseEnter={() => setActiveLetter(i)}
              onMouseLeave={() => setActiveLetter(null)}
              className={`px-5 py-4 bg-zinc-900/50 border backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 ${
                activeLetter === i
                  ? 'border-cyan-400 box-glow-cyan'
                  : 'border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <span className="block text-2xl font-black text-cyan-400 text-center">{item.letter}</span>
              <span className="block text-[10px] text-amber-400 uppercase tracking-widest mt-1 text-center font-bold">
                {item.word}
              </span>
              <AnimatePresence>
                {activeLetter === i && (
                  <motion.span
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="block text-[9px] text-zinc-500 mt-1 text-center overflow-hidden"
                  >
                    {item.desc}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Main Grid: Value Loop + Yield Meter */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
        <div className="lg:col-span-2">
          <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiGitBranch} /> The Value Loop
          </h2>
          <ValueLoop />
        </div>
        <div className="space-y-6">
          <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiTarget} /> Yield Target
          </h2>
          <YieldMeter />

          {/* Medallion Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-5"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiAward} className="text-amber-400" />
              </div>
              <div>
                <div className="text-sm font-bold text-zinc-100">Hammurabi Medallion</div>
                <div className="text-[10px] text-zinc-500 font-mono">User-Minted NSTF Asset</div>
              </div>
            </div>
            <div className="space-y-2 text-[11px] text-zinc-400">
              <div className="flex justify-between">
                <span className="text-zinc-600">Type</span>
                <span>Non-Speculative</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Backing</span>
                <span className="text-amber-400">Data Ad Revenue</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Gas Fees</span>
                <span className="text-emerald-400">ZERO</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Mining Pool</span>
                <span className="text-emerald-400">NONE</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Architecture Blueprint */}
      <div className="mb-16">
        <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiLayers} /> Architectural Blueprint
        </h2>
        <ArchBlueprint />
      </div>

      {/* Compliance Panel */}
      <div className="mb-16">
        <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiShield} /> Corporate Purchase Gateway — Compliance Layers
        </h2>
        <CompliancePanel />
      </div>

      {/* Sovereign Core Quote */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="p-8 bg-gradient-to-br from-zinc-900/60 to-zinc-950 border border-cyan-500/20 rounded-lg text-center mb-12"
      >
        <div className="text-xs text-cyan-400 tracking-widest uppercase mb-3">/// The Sovereign Core</div>
        <p className="text-sm md:text-base text-zinc-300 leading-relaxed max-w-3xl mx-auto">
          If you don't own the infrastructure, you can't own the data. 
          If you don't own the data, <span className="text-amber-400 font-bold">you are the product being sold.</span>
          <br /><br />
          The BSM-C framework flips the paradigm: 
          <span className="text-cyan-400"> you own the tools</span>, 
          <span className="text-amber-400"> you mint the Medallion</span>, 
          <span className="text-fuchsia-400"> you capture the revenue.</span>
        </p>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="flex flex-col sm:flex-row items-center justify-center gap-4"
      >
        <Link
          to="/showroom"
          className="px-8 py-4 bg-cyan-500 text-zinc-950 font-bold uppercase tracking-widest text-sm rounded hover:box-glow-cyan transition-all flex items-center gap-3"
        >
          <SafeIcon icon={FiIcons.FiCpu} />
          Configure Your Node
        </Link>
        <Link
          to="/manifesto"
          className="px-8 py-4 border border-zinc-700 text-zinc-300 font-bold uppercase tracking-widest text-sm rounded hover:border-amber-500/50 hover:text-amber-400 transition-all flex items-center gap-3"
        >
          <SafeIcon icon={FiIcons.FiBookOpen} />
          Read Full Manifesto
        </Link>
      </motion.div>

      {/* Footer Sig */}
      <div className="mt-12 text-center text-xs text-amber-400 italic text-glow-amber font-mono">
        // Keep the faith, the good exists.
      </div>
    </div>
  );
}